export const UI_UX_COURSES = [
  {
    id: 'ui-ux-design-for-developers',
    title: 'UI/UX Design for Developers',
    description: 'Learn design principles and create user-friendly interfaces with practical hands-on projects.',
    level: 'beginner',
    totalDuration: '6 hours',
    image: '/api/placeholder/400/300',
    prerequisites: ['Basic understanding of web development', 'HTML/CSS knowledge'],
    learningOutcomes: [
      'Understand fundamental design principles',
      'Create wireframes and prototypes',
      'Design responsive user interfaces',
      'Conduct user research and testing',
      'Use design tools effectively',
      'Apply accessibility best practices',
    ],
    modules: [
      {
        id: 'design-fundamentals',
        title: 'Design Fundamentals',
        description: 'Learn core design principles and visual hierarchy.',
        content: `# Design Fundamentals\n\n## Core Design Principles\n- Balance and symmetry\n- Contrast and emphasis\n- Alignment and proximity\n- Repetition and consistency\n\n## Color Theory\n- Color psychology\n- Color schemes and palettes\n- Accessibility and contrast ratios\n- Brand color systems\n\n## Typography\n- Font selection and pairing\n- Hierarchy and readability\n- Spacing and line height\n- Responsive typography`,
        duration: '90 minutes',
        difficulty: 'beginner',
        order: 1,
        estimatedTime: '90 minutes',
        objectives: [
          'Apply design principles to interfaces',
          'Choose appropriate colors and fonts',
          'Create visual hierarchy',
          'Understand user psychology',
        ],
        practice: {
          task: 'Design a landing page layout applying fundamental design principles.',
          hints: ['Focus on visual hierarchy', 'Use consistent spacing and typography'],
          estimatedTime: 120,
        },
        quiz: {
          questions: [
            {
              id: 'design-q1',
              question: 'What is the minimum contrast ratio for normal text according to WCAG AA?',
              options: ['3:1', '4.5:1', '7:1', '2.5:1'],
              correctAnswer: 1,
            },
          ],
        },
      },
      {
        id: 'prototyping-tools',
        title: 'Prototyping with Figma',
        description: 'Master Figma for UI design and prototyping.',
        content: `# Figma Mastery\n\n## Interface and Basics\n- Figma interface navigation\n- Frames, groups, and layers\n- Vector tools and shapes\n- Text and image handling\n\n## Components and Design Systems\n- Creating reusable components\n- Variants and properties\n- Auto-layout and constraints\n- Design token management\n\n## Prototyping\n- Interactive prototypes\n- Transitions and animations\n- Overlay and scroll behaviors\n- User testing with prototypes`,
        duration: '120 minutes',
        difficulty: 'intermediate',
        order: 2,
        estimatedTime: '120 minutes',
        objectives: [
          'Navigate Figma interface effectively',
          'Create reusable components',
          'Build interactive prototypes',
          'Collaborate with development teams',
        ],
        practice: {
          task: 'Create a complete mobile app prototype with interactive navigation.',
          hints: ['Use components for consistency', 'Add micro-interactions for better UX'],
          estimatedTime: 150,
        },
        quiz: {
          questions: [
            {
              id: 'figma-q1',
              question: 'What Figma feature allows you to create multiple versions of a component?',
              options: ['Instances', 'Variants', 'Groups', 'Frames'],
              correctAnswer: 1,
            },
          ],
        },
      },
      {
        id: 'user-research',
        title: 'User Research and Testing',
        description: 'Conduct user research and usability testing.',
        content: `# User Research & Testing\n\n## Research Methods\n- User interviews and surveys\n- Observational studies\n- Card sorting and tree testing\n- A/B testing methodology\n\n## User Personas\n- Creating data-driven personas\n- Persona validation and updates\n- Journey mapping\n- Pain point identification\n\n## Usability Testing\n- Test planning and setup\n- Moderated vs unmoderated testing\n- Task design and scenarios\n- Results analysis and reporting`,
        duration: '90 minutes',
        difficulty: 'intermediate',
        order: 3,
        estimatedTime: '90 minutes',
        objectives: [
          'Plan and conduct user research',
          'Create effective user personas',
          'Design and execute usability tests',
          'Analyze and present research findings',
        ],
        practice: {
          task: 'Conduct a complete usability test session and create a research report.',
          hints: ['Prepare specific tasks and scenarios', 'Focus on observing user behavior'],
          estimatedTime: 120,
        },
        quiz: {
          questions: [
            {
              id: 'research-q1',
              question: 'What is the ideal number of participants for qualitative usability testing?',
              options: ['3-5', '5-8', '10-15', '15-20'],
              correctAnswer: 0,
            },
          ],
        },
      },
    ],
    type: 'ui-ux-design',
  },
  {
    id: 'advanced-design-systems',
    title: 'Advanced Design Systems',
    description: 'Build scalable design systems with component libraries, design tokens, and accessibility guidelines.',
    level: 'advanced',
    totalDuration: '8 hours',
    image: '/api/placeholder/400/300',
    prerequisites: ['Figma proficiency', 'Understanding of component-based design', 'Basic frontend development knowledge'],
    learningOutcomes: [
      'Design and implement comprehensive design systems',
      'Create scalable component libraries',
      'Manage design tokens effectively',
      'Ensure accessibility compliance',
      'Collaborate with development teams on implementation',
    ],
    modules: [
      {
        id: 'design-system-foundations',
        title: 'Design System Foundations',
        description: 'Understand the principles and structure of effective design systems.',
        content: `# Design System Foundations\n\n## What is a Design System?\n- Components, patterns, and guidelines\n- Brand consistency and scalability\n- Developer-designer collaboration\n- Maintenance and evolution\n\n## Design System Architecture\n- Atomic design methodology\n- Component hierarchy\n- Documentation structure\n- Version control and releases\n\n## Design Tokens\n- Color, typography, spacing tokens\n- Multi-brand and theming\n- Platform-specific implementations\n- Token naming conventions`,
        duration: '120 minutes',
        difficulty: 'advanced',
        order: 1,
        estimatedTime: '120 minutes',
        objectives: [
          'Understand design system principles',
          'Plan design system architecture',
          'Define comprehensive design tokens',
        ],
        practice: {
          task: 'Create a design system foundation with comprehensive design tokens.',
          hints: ['Start with core tokens (colors, typography)', 'Consider multi-brand scenarios'],
          estimatedTime: 150,
        },
        quiz: {
          questions: [
            {
              id: 'design-system-q1',
              question: 'What are design tokens?',
              options: ['Design components', 'Visual design decisions stored as data', 'Figma plugins', 'Color palettes'],
              correctAnswer: 1,
            },
          ],
        },
      },
      {
        id: 'component-library-development',
        title: 'Component Library Development',
        description: 'Build comprehensive component libraries with proper documentation.',
        content: `# Component Library Development\n\n## Component Architecture\n- Base components and compositions\n- Props and state management\n- Responsive behavior\n- Accessibility considerations\n\n## Documentation\n- Component specifications\n- Usage guidelines and examples\n- Do's and don'ts\n- Code implementation guides\n\n## Quality Assurance\n- Component testing strategies\n- Cross-browser compatibility\n- Performance optimization\n- Maintenance workflows`,
        duration: '150 minutes',
        difficulty: 'advanced',
        order: 2,
        estimatedTime: '150 minutes',
        objectives: [
          'Design comprehensive component libraries',
          'Create detailed component documentation',
          'Implement quality assurance processes',
        ],
        practice: {
          task: 'Build a complete component library with documentation for a web application.',
          hints: ['Include all common UI patterns', 'Document accessibility features'],
          estimatedTime: 180,
        },
        quiz: {
          questions: [
            {
              id: 'component-lib-q1',
              question: 'What is the primary benefit of component composition over large monolithic components?',
              options: ['Faster loading', 'Better reusability and flexibility', 'Easier design', 'Smaller file sizes'],
              correctAnswer: 1,
            },
          ],
        },
      },
    ],
    type: 'ui-ux-design',
  },
  {
    id: 'user-research-data-driven-design',
    title: 'User Research & Data-Driven Design',
    description: 'Master advanced user research methodologies and integrate analytics for data-driven design decisions.',
    level: 'intermediate',
    totalDuration: '6 hours',
    image: '/api/placeholder/400/300',
    prerequisites: ['Basic UX knowledge', 'Understanding of user research principles', 'Basic analytics familiarity'],
    learningOutcomes: [
      'Conduct comprehensive user research studies',
      'Integrate quantitative and qualitative data',
      'Use analytics to inform design decisions',
      'Design and run effective A/B tests',
      'Create research-backed design recommendations',
    ],
    modules: [
      {
        id: 'advanced-research-methods',
        title: 'Advanced Research Methods',
        description: 'Master various user research methodologies and when to apply them.',
        content: `# Advanced Research Methods\n\n## Quantitative Methods\n- Surveys and questionnaires\n- Analytics and heatmaps\n- A/B testing and experimentation\n- Statistical significance\n\n## Qualitative Methods\n- In-depth interviews\n- Ethnographic studies\n- Diary studies\n- Focus groups\n\n## Mixed Methods\n- Triangulation techniques\n- Sequential explanatory design\n- Concurrent embedded design\n- Research planning and execution`,
        duration: '90 minutes',
        difficulty: 'intermediate',
        order: 1,
        estimatedTime: '90 minutes',
        objectives: [
          'Select appropriate research methodologies',
          'Design comprehensive research studies',
          'Combine quantitative and qualitative insights',
        ],
        practice: {
          task: 'Design a mixed-methods research study for a product redesign.',
          hints: ['Combine multiple research methods', 'Consider timeline and resources'],
          estimatedTime: 120,
        },
        quiz: {
          questions: [
            {
              id: 'research-methods-q1',
              question: 'When should you use qualitative research methods?',
              options: ['To measure user satisfaction', 'To understand user motivations', 'To test statistical significance', 'To reduce research costs'],
              correctAnswer: 1,
            },
          ],
        },
      },
      {
        id: 'analytics-integration',
        title: 'Analytics Integration for Design',
        description: 'Use analytics data to inform and validate design decisions.',
        content: `# Analytics for Design\n\n## Key Metrics\n- User engagement and retention\n- Conversion funnel analysis\n- Task completion rates\n- User flow optimization\n\n## Tools and Implementation\n- Google Analytics setup\n- Heatmap tools (Hotjar, Crazy Egg)\n- A/B testing platforms\n- Custom event tracking\n\n## Data Interpretation\n- Statistical significance\n- Correlation vs causation\n- Actionable insights\n- Reporting and recommendations`,
        duration: '90 minutes',
        difficulty: 'intermediate',
        order: 2,
        estimatedTime: '90 minutes',
        objectives: [
          'Set up analytics for design insights',
          'Interpret user behavior data',
          'Make data-driven design decisions',
        ],
        practice: {
          task: 'Analyze user analytics data and propose design improvements.',
          hints: ['Focus on user journey pain points', 'Consider both quantitative and qualitative factors'],
          estimatedTime: 120,
        },
        quiz: {
          questions: [
            {
              id: 'analytics-q1',
              question: 'What is the minimum confidence level typically used for A/B testing?',
              options: ['80%', '90%', '95%', '99%'],
              correctAnswer: 2,
            },
          ],
        },
      },
    ],
    type: 'ui-ux-design',
  },
  {
    id: 'mobile-first-ux-design',
    title: 'Mobile-First UX Design',
    description: 'Design exceptional mobile experiences with focus on touch interactions and progressive web apps.',
    level: 'intermediate',
    totalDuration: '6 hours',
    image: '/api/placeholder/400/300',
    prerequisites: ['UI/UX fundamentals', 'Responsive design knowledge', 'Mobile app familiarity'],
    learningOutcomes: [
      'Apply mobile-first design principles',
      'Design intuitive touch interactions',
      'Create progressive web app experiences',
      'Optimize for various screen sizes and orientations',
      'Implement mobile accessibility best practices',
    ],
    modules: [
      {
        id: 'mobile-design-patterns',
        title: 'Mobile Design Patterns',
        description: 'Master common mobile UI patterns and navigation systems.',
        content: `# Mobile Design Patterns\n\n## Navigation Patterns\n- Tab bars and bottom navigation\n- Hamburger menus and drawers\n- Card-based navigation\n- Gesture-based navigation\n\n## Interaction Patterns\n- Pull-to-refresh\n- Swipe actions and gestures\n- Long press interactions\n- Modal and overlay patterns\n\n## Content Patterns\n- Progressive disclosure\n- Infinite scroll vs pagination\n- Empty states and loading\n- Error handling patterns`,
        duration: '90 minutes',
        difficulty: 'intermediate',
        order: 1,
        estimatedTime: '90 minutes',
        objectives: [
          'Apply mobile-specific design patterns',
          'Design intuitive navigation systems',
          'Optimize content for mobile consumption',
        ],
        practice: {
          task: 'Design a complete mobile app interface using appropriate design patterns.',
          hints: ['Consider thumb-friendly touch targets', 'Use native platform conventions'],
          estimatedTime: 120,
        },
        quiz: {
          questions: [
            {
              id: 'mobile-patterns-q1',
              question: 'What is the minimum recommended touch target size for mobile interfaces?',
              options: ['40px', '44px', '48px', '52px'],
              correctAnswer: 1,
            },
          ],
        },
      },
      {
        id: 'progressive-web-apps',
        title: 'Progressive Web App Design',
        description: 'Design PWAs that provide native app-like experiences.',
        content: `# Progressive Web App Design\n\n## PWA Principles\n- Progressive enhancement\n- Responsive design\n- Connectivity independence\n- App-like interactions\n\n## Key Features\n- Service worker integration\n- Offline functionality\n- Push notifications\n- Home screen installation\n\n## Design Considerations\n- Loading states and skeleton screens\n- Offline content strategy\n- Native vs web conventions\n- Performance optimization`,
        duration: '90 minutes',
        difficulty: 'intermediate',
        order: 2,
        estimatedTime: '90 minutes',
        objectives: [
          'Design effective PWA experiences',
          'Plan offline functionality',
          'Optimize for performance and engagement',
        ],
        practice: {
          task: 'Design a PWA with offline capabilities and native app-like features.',
          hints: ['Consider offline-first design', 'Plan for various connection states'],
          estimatedTime: 120,
        },
        quiz: {
          questions: [
            {
              id: 'pwa-q1',
              question: 'What technology enables PWAs to work offline?',
              options: ['Web Workers', 'Service Workers', 'Local Storage', 'Cache API'],
              correctAnswer: 1,
            },
          ],
        },
      },
    ],
    type: 'ui-ux-design',
  },
  {
    id: 'design-strategy-business-impact',
    title: 'Design Strategy & Business Impact',
    description: 'Learn to align design decisions with business goals and demonstrate the ROI of design investments.',
    level: 'advanced',
    totalDuration: '6 hours',
    image: '/api/placeholder/400/300',
    prerequisites: ['UX design experience', 'Business fundamentals understanding', 'Project management basics'],
    learningOutcomes: [
      'Align design strategy with business objectives',
      'Measure and communicate design ROI',
      'Lead design initiatives at organizational level',
      'Manage stakeholder relationships effectively',
      'Create compelling design business cases',
    ],
    modules: [
      {
        id: 'design-business-alignment',
        title: 'Design-Business Alignment',
        description: 'Connect design decisions to business outcomes and metrics.',
        content: `# Design-Business Alignment\n\n## Business Fundamentals\n- Understanding business models\n- Key performance indicators (KPIs)\n- Customer acquisition and retention\n- Revenue impact measurement\n\n## Design Metrics\n- User experience metrics\n- Conversion rate optimization\n- Customer satisfaction scores\n- Task completion and efficiency\n\n## Strategic Planning\n- Design roadmap creation\n- Prioritization frameworks\n- Resource allocation\n- Success measurement`,
        duration: '90 minutes',
        difficulty: 'advanced',
        order: 1,
        estimatedTime: '90 minutes',
        objectives: [
          'Connect design to business metrics',
          'Create strategic design roadmaps',
          'Prioritize design initiatives effectively',
        ],
        practice: {
          task: 'Create a design strategy document linking UX improvements to business goals.',
          hints: ['Use specific metrics and projections', 'Include implementation timeline'],
          estimatedTime: 120,
        },
        quiz: {
          questions: [
            {
              id: 'design-strategy-q1',
              question: 'What is the most important factor when prioritizing design initiatives?',
              options: ['Design complexity', 'Business impact vs effort', 'Team preferences', 'Technology constraints'],
              correctAnswer: 1,
            },
          ],
        },
      },
      {
        id: 'design-leadership',
        title: 'Design Leadership and Communication',
        description: 'Develop leadership skills and effectively communicate design value to stakeholders.',
        content: `# Design Leadership\n\n## Leadership Skills\n- Vision and strategy development\n- Team building and mentorship\n- Cross-functional collaboration\n- Change management\n\n## Stakeholder Communication\n- Executive presentations\n- Design critique facilitation\n- Conflict resolution\n- Building design culture\n\n## Organizational Impact\n- Design system governance\n- Process optimization\n- Talent development\n- Industry influence`,
        duration: '90 minutes',
        difficulty: 'advanced',
        order: 2,
        estimatedTime: '90 minutes',
        objectives: [
          'Develop design leadership capabilities',
          'Communicate effectively with executives',
          'Build strong design culture',
        ],
        practice: {
          task: 'Prepare and deliver an executive presentation on design strategy and ROI.',
          hints: ['Focus on business outcomes', 'Use compelling visuals and data'],
          estimatedTime: 120,
        },
        quiz: {
          questions: [
            {
              id: 'design-leadership-q1',
              question: 'What is the key to successful design leadership?',
              options: ['Technical expertise', 'Creative vision', 'Business acumen and communication', 'Team management'],
              correctAnswer: 2,
            },
          ],
        },
      },
    ],
    type: 'ui-ux-design',
  },
];